'use client';
import { useEffect } from 'react';
import { useParams } from 'next/navigation';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function TeamMemberDetailPage() {
  const params = useParams();
  const slug = params?.slug as string;

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const teamMemberData: Record<string, any> = {
    'marcus-chen': {
      name: 'Marcus Chen',
      role: 'Creative Director',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_12672b149-1763294392419.png",
      imageAlt: 'Professional headshot of Marcus Chen in dark suit',
      bio: 'Marcus brings 15 years of creative leadership experience to Best Odds Corp. His work has been recognized with multiple industry awards and featured in major publications.',
      expertise: ['Creative Strategy', 'Art Direction', 'Brand Development', 'Campaign Concepting'],
      experience: '15+ years',
      education: 'MFA in Design, Rhode Island School of Design',
      social: {
        linkedin: '#',
        twitter: '#',
        instagram: '#'
      }
    },
    'sarah-rodriguez': {
      name: 'Sarah Rodriguez',
      role: 'Head of Production',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bb5d2cb2-1763294526193.png',
      imageAlt: 'Professional headshot of Sarah Rodriguez smiling',
      bio: 'Sarah oversees all production operations at Best Odds Corp, ensuring every project is delivered on time and exceeds quality standards.',
      expertise: ['Video Production', 'Project Management', 'Post-Production', 'Team Leadership'],
      experience: '12+ years',
      education: 'BA in Film Production, USC School of Cinematic Arts',
      social: {
        linkedin: '#',
        twitter: '#',
        instagram: '#'
      }
    },
    'david-park': {
      name: 'David Park',
      role: 'Strategy Lead',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1de24a543-1763292578805.png',
      imageAlt: 'Professional headshot of David Park in casual attire',
      bio: 'David leads strategic planning for all client engagements, combining data-driven insights with creative thinking to drive business results.',
      expertise: ['Business Strategy', 'Market Research', 'Competitive Analysis', 'Growth Planning'],
      experience: '10+ years',
      education: 'MBA, Stanford Graduate School of Business',
      social: {
        linkedin: '#',
        twitter: '#',
        instagram: '#'
      }
    },
    'elena-martinez': {
      name: 'Elena Martinez',
      role: 'Tech Director',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1afba9d0c-1763297586955.png',
      imageAlt: 'Professional headshot of Elena Martinez with glasses',
      bio: 'Elena leads our technology practice, building cutting-edge digital experiences and custom software solutions for clients.',
      expertise: ['Web Development', 'Mobile Apps', 'Software Architecture', 'Technical Strategy'],
      experience: '14+ years',
      education: 'MS in Computer Science, MIT',
      social: {
        linkedin: '#',
        twitter: '#',
        instagram: '#'
      }
    }
  };

  const member = teamMemberData[slug] || {
    name: 'Team Member',
    role: 'Position',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d90a96ad-1763299909299.png',
    imageAlt: 'Professional headshot',
    bio: 'Team member bio goes here.',
    expertise: ['Skill 1', 'Skill 2', 'Skill 3', 'Skill 4'],
    experience: '10+ years',
    education: 'Degree, University',
    social: {
      linkedin: '#',
      twitter: '#',
      instagram: '#'
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="reveal">
                <div className="aspect-square overflow-hidden border border-border">
                  <AppImage
                    src={member.image}
                    alt={member.imageAlt}
                    className="w-full h-full object-cover grayscale" />
                  
                </div>
              </div>
              <div>
                <div className="reveal">
                  <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Team Member</span>
                  <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-4">{member.name}</h1>
                  <p className="text-xl text-muted-foreground mb-8">{member.role}</p>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-8">{member.bio}</p>
                  <div className="flex gap-4">
                    <a
                      href={member.social.linkedin}
                      className="w-10 h-10 border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                      aria-label="LinkedIn">
                      
                      <Icon name="BuildingOfficeIcon" size={18} />
                    </a>
                    <a
                      href={member.social.twitter}
                      className="w-10 h-10 border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                      aria-label="Twitter">
                      
                      <Icon name="ChatBubbleLeftIcon" size={18} />
                    </a>
                    <a
                      href={member.social.instagram}
                      className="w-10 h-10 border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                      aria-label="Instagram">
                      
                      <Icon name="CameraIcon" size={18} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Details Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="reveal">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Expertise</span>
                <div className="space-y-3">
                  {member.expertise.map((skill: string, index: number) =>
                  <div key={index} className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" size={16} className="text-primary" />
                      <span className="text-body-sm">{skill}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="reveal">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Experience</span>
                <p className="text-body">{member.experience}</p>
              </div>
              <div className="reveal">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Education</span>
                <p className="text-body">{member.education}</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6">
          <div className="max-w-[1600px] mx-auto text-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Want to Work Together?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Book a consultation to discuss your project with our team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/book"
                  className="inline-block px-8 py-4 bg-primary text-primary-foreground text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors font-semibold border-glow">
                  
                  Book Consultation
                </Link>
                <Link
                  href="/team"
                  className="inline-block px-8 py-4 border border-border text-foreground text-xs uppercase tracking-widest hover:border-primary hover:text-primary transition-colors font-semibold">
                  
                  View Full Team
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>);

}